import {
  Collapsible,
  Input,
  InputGroup,
  InputGroupLabel,
  Label,
  Message,
} from '@sbab-ui/components'
import { CloseIcon, QuestionMarkIcon } from '@sbab-ui/icons'
import { useRef, useState } from 'react'
import FeedbackButtons from './FeedbackButtons'
import { FieldName } from './DebouncedInputs'
import { track } from '../../lib/tracking'
import { camelToKebab } from '../../lib/utils'

interface Props {
  id: FieldName
  value: string
  valueChangeHandler: (field: FieldName, value: string) => void
  label: string
  suffix?: string
  errorMessage?: string
  tooltip?: React.ReactNode
  tooltipAriaLabel?: string
}

export default function CustomInput({
  id,
  value,
  valueChangeHandler,
  label,
  suffix,
  errorMessage,
  tooltip,
  tooltipAriaLabel,
}: Readonly<Props>) {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  function onChangeHandler() {
    // Use ref to access the input element
    const inputElement = inputRef.current
    if (!inputElement) return // Exit if the input element is not yet initialized

    const originalValue = inputElement.value
    const selectionStart = inputElement.selectionStart // Cursor position before change

    const numericValue = originalValue.replace(/\D/g, '')

    let newValue = ''

    if (/^0+$/.test(numericValue)) {
      newValue = numericValue
    } else if (numericValue) {
      newValue = parseInt(numericValue, 10)
        .toLocaleString('sv-SE', { maximumFractionDigits: 0 })
        .replace(/,/g, '')
    }

    valueChangeHandler(id, newValue)

    requestAnimationFrame(() => {
      if (selectionStart !== null) {
        const diff = originalValue.length - newValue.length
        const newPosition = Math.max(0, selectionStart - diff)
        inputElement.setSelectionRange(newPosition, newPosition)
      }
    })
  }

  function onKeyDownHandler(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace') {
      handleBackspace(event)
    }
  }

  function handleBackspace(event: React.KeyboardEvent<HTMLInputElement>) {
    const inputElement = inputRef.current
    if (!inputElement || !isSingleCharDeletion(inputElement)) return

    const charBeforeCursor = getCharBeforeCursor(inputElement)

    if (isWhitespace(charBeforeCursor)) {
      deleteCharBeforeCursor(event, inputElement)
    }
  }

  function isSingleCharDeletion(inputElement: HTMLInputElement): boolean {
    const { selectionStart, selectionEnd } = inputElement
    return (
      selectionStart !== null &&
      selectionEnd !== null &&
      selectionStart === selectionEnd &&
      selectionStart > 0
    )
  }

  function getCharBeforeCursor(inputElement: HTMLInputElement): string {
    const { value, selectionStart } = inputElement
    return value.charAt(selectionStart! - 1) // The non-null assertion is safe here due to the preceding checks.
  }

  function isWhitespace(character: string): boolean {
    return /\s/.test(character)
  }

  function deleteCharBeforeCursor(
    event: React.KeyboardEvent<HTMLInputElement>,
    inputElement: HTMLInputElement,
  ) {
    const { value, selectionStart } = inputElement

    // Find the position of the last non-space character before the cursor.
    let lastNonSpaceCharIndex = selectionStart! - 1
    while (lastNonSpaceCharIndex >= 0 && /\s/.test(value.charAt(lastNonSpaceCharIndex))) {
      lastNonSpaceCharIndex--
    }

    const newPosition = lastNonSpaceCharIndex + 1 // Position cursor after the last non-space character.
    const newValue =
      value.substring(0, lastNonSpaceCharIndex + 1) + value.substring(selectionStart!)

    // Update the input's value as per your application's state management.
    valueChangeHandler(id, newValue) // Assuming this function updates the input value in your component's state or props.

    // Prevent the default backspace action to avoid interference.
    event.preventDefault()

    // Adjust cursor position after deletion.
    adjustCursorPosition(inputElement, newPosition)
  }

  function adjustCursorPosition(inputElement: HTMLInputElement, newPosition: number) {
    requestAnimationFrame(() => {
      inputElement.setSelectionRange(newPosition, newPosition)
    })
  }

  return (
    <div className={`-m-2 flex flex-col p-2 ${open ? 'bg-surface-banana' : ''}`}>
      <Collapsible.Root
        open={open}
        onOpenChange={(prevValue) => {
          track(open ? 'tooltip.collapsed' : 'tooltip.expanded', camelToKebab(id))
          setOpen(prevValue)
        }}
      >
        <div className="flex items-center justify-between">
          <Label block htmlFor={id}>
            {label}
          </Label>
          {tooltip && (
            <>
              {/* Chromium Bug Workaround */}
              <div className="self-start">
                <Collapsible.Trigger
                  className="ml-2"
                  aria-label={tooltipAriaLabel}
                  aria-expanded={open}
                >
                  {open ? <CloseIcon /> : <QuestionMarkIcon />}
                </Collapsible.Trigger>
              </div>
            </>
          )}
        </div>
        <Collapsible.Content animated={true}>
          {tooltip}
          <FeedbackButtons id={id} />
          <div className="h-2" />
        </Collapsible.Content>
      </Collapsible.Root>
      <div className="h-2" />
      <InputGroup block>
        <Input
          ref={inputRef}
          value={value}
          onChange={onChangeHandler}
          onKeyDown={onKeyDownHandler}
          id={id}
          data-label={id}
          className="!w-full"
          invalid={!!errorMessage}
          inputMode="numeric"
        />
        {suffix && <InputGroupLabel>{suffix}</InputGroupLabel>}
      </InputGroup>
      {errorMessage ? (
        <Message tone="critical" className="mt-2" data-cy={`${camelToKebab(id)}-error-msg`}>
          {errorMessage}
        </Message>
      ) : null}
    </div>
  )
}
